import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, RefreshCw, Shield, Flame, Search,
  Brain, Star, TrendingUp, DollarSign, Activity, ChevronDown,
  Sparkles, Zap, Target, AlertTriangle, Wallet, Clock, Crosshair,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const RISK_CONFIG = {
  bajo: { color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', label: 'Bajo' },
  medio: { color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30', label: 'Medio' },
  alto: { color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30', label: 'Alto' },
};

const LotteryCard = ({ lottery, token, index }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  const analyze = useCallback(async () => {
    if (data || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/tauro/lottery/${lottery.id}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 90000,
      });
      setData(res.data);
      setExpanded(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error analizando');
      toast.error(`Error ${lottery.name}`);
    }
    setLoading(false);
  }, [token, lottery.id, lottery.name, data, loading]);

  const toggle = () => {
    if (data) {
      setExpanded(!expanded);
    } else {
      analyze();
    }
  };

  const risk = data?.risk ? RISK_CONFIG[data.risk] || RISK_CONFIG.medio : null;
  // V5: usar sniper/alerta confidences si existen; fallback a confidence antiguo
  const sniperConf = data?.sniper_confidence ?? data?.confidence ?? 0;
  const alertaConf = data?.alerta_confidence ?? data?.confidence ?? 0;
  const sniper3 = data?.sniper_3 || data?.combo || [];
  const alerta10 = data?.alerta_10 || [];
  const sniperColor = sniperConf >= 25 ? 'text-emerald-400' : sniperConf >= 15 ? 'text-amber-400' : 'text-slate-400';

  return (
    <div className="rounded-xl bg-slate-900/70 border border-slate-800/50 hover:border-orange-700/40 transition-all overflow-hidden"
      data-testid={`tauro-${lottery.id}`}
      style={{ animation: `fadeIn 0.3s ease-out ${index * 40}ms backwards` }}>
      <div role="button" tabIndex={0} className="w-full p-4 flex items-center gap-4 text-left cursor-pointer" onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(); }}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="w-2 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: lottery.color || '#22c55e' }} />
            <h3 className="text-white font-bold text-sm truncate">{lottery.name}</h3>
            <Badge variant="outline" className="text-[9px] py-0 border-slate-700 text-slate-500">{lottery.company}</Badge>
          </div>
          <div className="flex items-center gap-2 ml-4 mt-0.5 text-xs">
            <span className="text-slate-500">{lottery.draw_time}</span>
            {data && (
              <>
                <span className="text-slate-600">·</span>
                <span className="text-orange-300 flex items-center gap-1">
                  <Flame size={10} />
                  {data.hot_terminals.map(t => `x${t}`).join(' ')}
                </span>
                <span className="text-slate-600">·</span>
                <span className="text-slate-400">Sócrates: <strong className="text-purple-300">{data.llm_agreement}</strong></span>
              </>
            )}
          </div>
        </div>

        {data && (
          <div className="flex items-center gap-2">
            {sniper3.map((n, i) => (
              <div key={`tc-${n}`}
                className={`rounded-xl flex items-center justify-center shadow-lg ${
                  i === 0 && n === data.star_number
                    ? 'w-14 h-14 bg-gradient-to-br from-orange-400 via-red-500 to-rose-600 shadow-red-500/40 relative'
                    : 'w-11 h-11 bg-gradient-to-br from-orange-600/70 to-rose-700/70'
                }`}>
                {i === 0 && n === data.star_number && (
                  <Star size={10} className="absolute -top-1 -right-1 text-yellow-300 fill-yellow-300" />
                )}
                <span className="text-white font-black text-base">{String(n).padStart(2, '0')}</span>
              </div>
            ))}
            <div className={`text-right ${sniperColor}`}>
              <div className="text-lg font-black">{sniperConf}%</div>
              <div className="text-[8px] uppercase">sniper</div>
            </div>
          </div>
        )}

        {!data && !loading && (
          <Button size="sm" variant="outline" className="border-orange-700/40 text-orange-400" data-testid={`tauro-analyze-${lottery.id}`}>
            <Brain size={14} className="mr-1" /> Analizar
          </Button>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-orange-400">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-xs">Sócrates pensando...</span>
          </div>
        )}
      </div>

      {error && (
        <div className="px-4 pb-3 text-xs text-red-400 flex items-center gap-2">
          <AlertTriangle size={12} /> {error}
        </div>
      )}

      {data && expanded && (
        <div className="px-4 pb-4 border-t border-slate-800/50 space-y-3" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          {/* ===== V5 SNIPER PREMIO MAYOR — Bloque principal ===== */}
          <div className="mt-3 p-4 rounded-xl bg-gradient-to-br from-red-950/60 via-orange-950/40 to-amber-950/30 border-2 border-orange-500/40 shadow-lg shadow-orange-900/20" data-testid={`tauro-sniper-${lottery.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target size={18} className="text-orange-400" />
                <div>
                  <div className="text-orange-300 font-black text-base tracking-tight">SNIPER PREMIO MAYOR</div>
                  <div className="text-orange-500/70 text-[10px] uppercase tracking-widest">3 numeros laser · Posicion 1</div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-2xl font-black ${sniperColor}`}>{sniperConf}%</div>
                <div className="text-orange-500/60 text-[9px] uppercase">confianza</div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 my-4">
              {sniper3.map((n, i) => (
                <div key={`sn-${n}-${i}`} className="relative">
                  <div className={`w-20 h-20 rounded-2xl flex items-center justify-center shadow-xl ${
                    n === data.star_number
                      ? 'bg-gradient-to-br from-yellow-400 via-orange-500 to-red-600 shadow-orange-500/60 ring-2 ring-yellow-300/50'
                      : 'bg-gradient-to-br from-orange-600 to-rose-700 shadow-rose-700/40'
                  }`}>
                    <span className="text-white font-black text-3xl">{String(n).padStart(2, '0')}</span>
                  </div>
                  {n === data.star_number && (
                    <div className="absolute -top-2 -right-2 bg-yellow-300 text-amber-900 text-[9px] font-black px-1.5 py-0.5 rounded-full shadow-md">
                      <Star size={9} className="inline -mt-0.5 mr-0.5 fill-amber-900" />ESTRELLA
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
              <div className="p-1.5 rounded bg-slate-900/40">
                <div className="text-orange-400 font-black text-sm">{data.llm_agreement}</div>
                <div className="text-slate-500">Acuerdo núcleos</div>
              </div>
              <div className="p-1.5 rounded bg-slate-900/40">
                <div className="text-orange-400 font-black text-sm">{data.consensus_score}/100</div>
                <div className="text-slate-500">Consenso</div>
              </div>
              <div className="p-1.5 rounded bg-slate-900/40">
                <div className={`font-black text-sm ${
                  data.historic_stats?.sniper_3_pos1_rate >= 10 ? 'text-emerald-400' :
                  data.historic_stats?.sniper_3_pos1_rate >= 5 ? 'text-amber-400' : 'text-slate-400'
                }`}>{data.historic_stats?.sniper_3_pos1_rate ?? '—'}%</div>
                <div className="text-slate-500">Histórico Pos-1</div>
              </div>
            </div>

            <p className="text-orange-200/70 text-[10px] mt-3 leading-relaxed text-center">
              Los 3 núcleos Sócrates analizaron exclusivamente el historial de <strong className="text-orange-300">Posicion 1 (Premio Mayor)</strong> de esta loteria.
              Probabilidad base 1% → Sniper sube a ~{(sniperConf/10).toFixed(1)}x.
            </p>
          </div>

          {/* ===== SNIPER POR POSICIÓN — 1 número para cada posición ===== */}
          {Array.isArray(data.sniper_positional) && data.sniper_positional.filter(n => n !== null && n !== undefined).length > 0 && (
            <div className="mt-3 p-4 rounded-xl bg-gradient-to-br from-indigo-950/60 via-blue-950/40 to-sky-950/30 border-2 border-sky-500/40 shadow-lg shadow-sky-900/20" data-testid={`tauro-positional-${lottery.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <Crosshair size={18} className="text-sky-400" />
                <div>
                  <div className="text-sky-300 font-black text-base tracking-tight">SNIPER POR POSICIÓN</div>
                  <div className="text-sky-500/70 text-[10px] uppercase tracking-widest">1 número para cada posición</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {['primera', 'segunda', 'tercera'].map((pos, idx) => {
                  const num = data.sniper_positional[idx];
                  const info = data.positional_by_pos?.[pos];
                  if (num === null || num === undefined) return (
                    <div key={pos} className="text-center opacity-40">
                      <div className="text-sky-500/70 text-[10px] uppercase font-bold mb-1">{pos}</div>
                      <div className="text-slate-500 text-sm">—</div>
                    </div>
                  );
                  return (
                    <div key={pos} className="text-center" data-testid={`positional-${pos}-${lottery.id}`}>
                      <div className="text-sky-400 text-[10px] uppercase font-black mb-1.5 tracking-wider">{pos}</div>
                      <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center bg-gradient-to-br from-sky-600 to-blue-700 shadow-lg shadow-sky-700/40">
                        <span className="text-white font-black text-2xl">{String(num).padStart(2, '0')}</span>
                      </div>
                      {info?.alternates?.length > 0 && (
                        <div className="mt-1.5 text-[9px] text-sky-300/60">
                          alt: {info.alternates.map(a => String(a).padStart(2, '0')).join(' · ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-sky-200/60 text-[10px] mt-3 leading-relaxed text-center">
                Cada número está optimizado con las señales estadísticas propias de su posición
                (frecuencia, recencia, Markov y gap). Son sugerencias basadas en el historial, no una garantía.
              </p>
            </div>
          )}

          {/* ===== V5 ALERTA 10 — 10 números cualquier posición ===== */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-950/40 to-teal-950/30 border-2 border-emerald-500/30" data-testid={`tauro-alerta-${lottery.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-emerald-400" />
                <div>
                  <div className="text-emerald-300 font-black text-base">ALERTA 10</div>
                  <div className="text-emerald-500/70 text-[10px] uppercase tracking-widest">10 numeros · 1ro / 2do / 3ro</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black text-emerald-400">{alertaConf}%</div>
                <div className="text-emerald-500/60 text-[9px] uppercase">confianza</div>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-2 mb-3">
              {alerta10.map((n, i) => {
                const isSniper = sniper3.includes(n);
                const isStar = n === data.star_number;
                return (
                  <div key={`al-${n}-${i}`} className={`aspect-square rounded-xl flex flex-col items-center justify-center font-black border-2 ${
                    isStar ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white border-yellow-300 shadow-lg shadow-orange-500/40'
                    : isSniper ? 'bg-orange-500/30 text-orange-100 border-orange-500/60'
                    : 'bg-emerald-900/50 text-emerald-100 border-emerald-700/40'
                  }`}>
                    <span className="text-xl leading-none">{String(n).padStart(2, '0')}</span>
                    {(isSniper || isStar) && (
                      <span className="text-[7px] mt-0.5 opacity-75 uppercase tracking-tight">
                        {isStar ? 'estrella' : 'sniper'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-2 text-center text-[10px]">
              <div className="p-1.5 rounded bg-slate-900/40">
                <div className={`font-black text-sm ${
                  data.historic_stats?.alerta_10_pos1_rate >= 15 ? 'text-emerald-400' :
                  data.historic_stats?.alerta_10_pos1_rate >= 8 ? 'text-amber-400' : 'text-slate-400'
                }`}>{data.historic_stats?.alerta_10_pos1_rate ?? '—'}%</div>
                <div className="text-slate-500">Hit Premio Mayor</div>
              </div>
              <div className="p-1.5 rounded bg-slate-900/40">
                <div className={`font-black text-sm ${
                  data.historic_stats?.alerta_10_any_rate >= 25 ? 'text-emerald-400' :
                  data.historic_stats?.alerta_10_any_rate >= 15 ? 'text-amber-400' : 'text-slate-400'
                }`}>{data.historic_stats?.alerta_10_any_rate ?? '—'}%</div>
                <div className="text-slate-500">Hit Cualquier Posicion</div>
              </div>
            </div>

            <p className="text-emerald-200/70 text-[10px] mt-3 leading-relaxed text-center">
              Cobertura para los 3 premios (1ro/2do/3ro). Incluye los 3 sniper + 7 numeros complementarios diversificados.
            </p>
          </div>

          {/* ===== V6 LAB MATEMATICO — 5 capas analiticas ===== */}
          {data.math_v6?.backtest && (
            <details className="rounded-xl bg-gradient-to-br from-violet-950/40 via-fuchsia-950/30 to-purple-950/30 border-2 border-violet-500/30" data-testid={`tauro-mathv6-${lottery.id}`}>
              <summary className="cursor-pointer p-3 flex items-center justify-between hover:bg-violet-900/20">
                <div className="flex items-center gap-2">
                  <Activity className="text-violet-400" size={16} />
                  <div>
                    <div className="text-violet-300 font-bold text-sm">Lab Matemático V6 — 5 capas analíticas</div>
                    <div className="text-violet-500/70 text-[10px]">Markov · Gaps · Confluencia · 2-grams · Combinado</div>
                  </div>
                </div>
                <Badge className="bg-violet-500/20 border border-violet-400/50 text-violet-200 text-[10px] font-bold">
                  Alerta {data.math_v6.backtest.combined_v6_alerta_any_rate}%
                </Badge>
              </summary>
              <div className="p-3 border-t border-violet-700/30 space-y-3">
                {/* Backtest comparativo */}
                <div className="grid grid-cols-4 gap-1.5">
                  <div className="p-2 rounded bg-slate-900/50 text-center">
                    <div className={`font-black text-sm ${data.math_v6.backtest.markov_p1_pos1_rate >= 5 ? 'text-emerald-400' : 'text-slate-400'}`}>
                      {data.math_v6.backtest.markov_p1_pos1_rate}%
                    </div>
                    <div className="text-violet-400/70 text-[8px] uppercase">Markov P1</div>
                  </div>
                  <div className="p-2 rounded bg-slate-900/50 text-center">
                    <div className={`font-black text-sm ${data.math_v6.backtest.gaps_any_rate >= 25 ? 'text-emerald-400' : 'text-slate-400'}`}>
                      {data.math_v6.backtest.gaps_any_rate}%
                    </div>
                    <div className="text-violet-400/70 text-[8px] uppercase">Gaps any</div>
                  </div>
                  <div className="p-2 rounded bg-slate-900/50 text-center">
                    <div className={`font-black text-sm ${data.math_v6.backtest.confluence_any_rate >= 30 ? 'text-emerald-400' : 'text-slate-400'}`}>
                      {data.math_v6.backtest.confluence_any_rate}%
                    </div>
                    <div className="text-violet-400/70 text-[8px] uppercase">Confluencia</div>
                  </div>
                  <div className="p-2 rounded bg-slate-900/50 text-center">
                    <div className={`font-black text-sm ${data.math_v6.backtest.markov_2g_any_rate >= 30 ? 'text-emerald-400' : 'text-slate-400'}`}>
                      {data.math_v6.backtest.markov_2g_any_rate}%
                    </div>
                    <div className="text-violet-400/70 text-[8px] uppercase">Markov 2g</div>
                  </div>
                </div>

                {/* Combinado */}
                <div className="p-2 rounded-lg bg-gradient-to-r from-violet-900/40 to-fuchsia-900/30 border border-violet-500/40">
                  <div className="text-violet-300 text-[10px] font-bold mb-1 uppercase">Combinado V6 (las 4 capas juntas)</div>
                  <div className="grid grid-cols-3 gap-1.5 text-center">
                    <div className="p-1.5 rounded bg-slate-900/60">
                      <div className="text-white font-black text-sm">{data.math_v6.backtest.combined_v6_sniper_pos1_rate}%</div>
                      <div className="text-violet-400/60 text-[8px]">Sniper Pos-1</div>
                    </div>
                    <div className="p-1.5 rounded bg-slate-900/60">
                      <div className="text-white font-black text-sm">{data.math_v6.backtest.combined_v6_sniper_any_rate}%</div>
                      <div className="text-violet-400/60 text-[8px]">Sniper any</div>
                    </div>
                    <div className="p-1.5 rounded bg-emerald-950/60 border border-emerald-500/40">
                      <div className="text-emerald-300 font-black text-sm">{data.math_v6.backtest.combined_v6_alerta_any_rate}%</div>
                      <div className="text-emerald-400/70 text-[8px]">Alerta any</div>
                    </div>
                  </div>
                </div>

                {/* Top 10 combinado */}
                {data.math_v6.top_30_combined?.length > 0 && (
                  <div>
                    <div className="text-violet-300 text-[10px] font-bold mb-1 uppercase">Top 10 score combinado V6</div>
                    <div className="flex gap-1 flex-wrap">
                      {data.math_v6.top_30_combined.slice(0, 10).map((n, i) => {
                        const inSniper = data.sniper_3?.includes(n);
                        const inAlerta = data.alerta_10?.includes(n);
                        return (
                          <div key={`v6-${n}-${i}`} className={`px-2 py-1 rounded text-xs font-bold border ${
                            inSniper ? 'bg-orange-500/30 text-orange-100 border-orange-500/50'
                            : inAlerta ? 'bg-emerald-500/20 text-emerald-100 border-emerald-500/40'
                            : 'bg-violet-500/15 text-violet-200 border-violet-500/30'
                          }`}>
                            {String(n).padStart(2, '0')}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <p className="text-violet-200/60 text-[10px] text-center leading-relaxed">
                  Cada capa analiza la lotería desde un ángulo matemático distinto. <strong className="text-violet-300">Combinadas</strong> producen los anchors de Sniper y Alerta 10.
                  Backtest sobre {data.math_v6.backtest.draws_tested} sorteos reales.
                </p>
              </div>
            </details>
          )}

          {/* INTELIGENCIA ADAPTATIVA — NUEVO V4 */}
          {data.adaptive_intelligence?.available && (
            <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-indigo-950/40 to-violet-950/30 border border-indigo-700/40"
              data-testid={`tauro-intel-${lottery.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Brain size={14} className="text-indigo-400" />
                  <span className="text-indigo-300 font-bold text-sm">Inteligencia Adaptativa</span>
                </div>
                <Badge variant="outline" className={`text-[10px] py-0 ${
                  data.adaptive_intelligence.mode?.mode === 'caliente' ? 'border-orange-500/50 text-orange-300'
                  : data.adaptive_intelligence.mode?.mode === 'frio' ? 'border-cyan-500/50 text-cyan-300'
                  : data.adaptive_intelligence.mode?.mode === 'transicion' ? 'border-red-500/50 text-red-300'
                  : 'border-slate-500/50 text-slate-300'
                } font-bold`}>
                  Modo: {data.adaptive_intelligence.mode?.mode}
                </Badge>
              </div>

              <p className="text-slate-300 text-[11px] mb-2 leading-relaxed">
                {data.adaptive_intelligence.mode?.message}
              </p>

              {/* Best signal */}
              {data.adaptive_intelligence.best_historical_signal && (
                <div className="mb-2 p-2 rounded bg-slate-900/60">
                  <div className="text-[10px] text-slate-500 mb-0.5">Senal mas confiable en esta loteria:</div>
                  <div className="flex items-center justify-between">
                    <span className="text-indigo-300 font-bold text-xs">
                      {data.adaptive_intelligence.best_historical_signal.name}
                    </span>
                    <span className="text-emerald-400 font-black text-sm">
                      {data.adaptive_intelligence.best_historical_signal.rate}%
                    </span>
                  </div>
                </div>
              )}

              {/* Specific combos */}
              {data.adaptive_intelligence.specific_combos && (
                <div className="space-y-1.5">
                  <div className="text-[10px] text-slate-500 font-bold uppercase">Combos especificos:</div>

                  <div className="p-2 rounded bg-slate-900/50">
                    <div className="text-[9px] text-amber-400 mb-0.5">⭐ Combo 5 mas probable</div>
                    <div className="flex gap-1 flex-wrap">
                      {data.adaptive_intelligence.specific_combos.combo_5?.map(n => (
                        <span key={`c5-${n}`} className="px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-200 font-bold text-xs">
                          {String(n).padStart(2, '0')}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="p-2 rounded bg-slate-900/50">
                    <div className="text-[9px] text-emerald-400 mb-0.5">📊 Combo 7 (mayor cobertura)</div>
                    <div className="flex gap-1 flex-wrap">
                      {data.adaptive_intelligence.specific_combos.combo_7?.map(n => (
                        <span key={`c7-${n}`} className="px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 font-bold text-xs">
                          {String(n).padStart(2, '0')}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="p-2 rounded bg-slate-900/50">
                    <div className="text-[9px] text-purple-400 mb-0.5">🏆 Tripleta Frecuente (all-time)</div>
                    <div className="flex gap-1">
                      {data.adaptive_intelligence.specific_combos.tripleta_frecuente?.map(n => (
                        <span key={`tf-${n}`} className="px-2 py-0.5 rounded bg-purple-500/15 border border-purple-500/30 text-purple-200 font-bold text-xs">
                          {String(n).padStart(2, '0')}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="p-2 rounded bg-slate-900/50">
                    <div className="text-[9px] text-cyan-400 mb-0.5">❄️ Cold Comeback (regresion a la media)</div>
                    <div className="flex gap-1 flex-wrap">
                      {data.adaptive_intelligence.specific_combos.cold_comeback_5?.map(n => (
                        <span key={`cc-${n}`} className="px-2 py-0.5 rounded bg-cyan-500/15 border border-cyan-500/30 text-cyan-200 font-bold text-xs">
                          {String(n).padStart(2, '0')}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <details className="mt-2">
                <summary className="cursor-pointer text-[10px] text-indigo-400 hover:text-indigo-300">
                  Ver performance detallada de cada señal
                </summary>
                <div className="mt-1 space-y-0.5">
                  {Object.entries(data.adaptive_intelligence.signal_performance || {})
                    .sort((a, b) => b[1].rate - a[1].rate)
                    .map(([sig, perf]) => (
                      <div key={sig} className="flex items-center justify-between text-[10px] p-1 rounded bg-slate-900/30">
                        <span className="text-slate-400">{sig}</span>
                        <span className={`font-bold ${perf.rate >= 50 ? 'text-emerald-400' : perf.rate >= 35 ? 'text-amber-400' : 'text-slate-500'}`}>
                          {perf.rate}% ({perf.samples} samples)
                        </span>
                      </div>
                    ))}
                </div>
              </details>

              {data.tauro_track_record && (
                <div className="mt-2 p-2 rounded bg-gradient-to-r from-emerald-950/60 to-cyan-950/40 border border-emerald-700/40">
                  <div className="text-[10px] text-emerald-300 font-bold mb-1">📈 Track Record de Tauro (ultimos 14 dias)</div>
                  <div className="grid grid-cols-4 gap-1 text-center text-[10px]">
                    <div>
                      <div className="text-white font-bold">{data.tauro_track_record.combo_hit_rate}%</div>
                      <div className="text-slate-500 text-[8px]">combo</div>
                    </div>
                    <div>
                      <div className="text-white font-bold">{data.tauro_track_record.cuadricula_25_hit_rate}%</div>
                      <div className="text-slate-500 text-[8px]">cuad 25</div>
                    </div>
                    <div>
                      <div className="text-white font-bold">{data.tauro_track_record.cobertura_42_hit_rate}%</div>
                      <div className="text-slate-500 text-[8px]">cob 42</div>
                    </div>
                    <div>
                      <div className="text-white font-bold">{data.tauro_track_record.star_hit_rate}%</div>
                      <div className="text-slate-500 text-[8px]">estrella</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* HISTORIC STATS — honestidad */}
          {data.historic_stats && (
            <div className="mt-3 p-3 rounded-lg bg-slate-950/40 border border-slate-700/50" data-testid={`tauro-historic-${lottery.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <Activity size={13} className="text-cyan-400" />
                <span className="text-cyan-300 font-bold text-xs">Acierto Real en los Ultimos {data.historic_stats.draws_tested} Sorteos</span>
              </div>
              <div className="grid grid-cols-5 gap-1.5 text-center">
                <div className="p-1.5 rounded bg-slate-900/60">
                  <div className={`font-black text-sm ${data.historic_stats.combo_3_rate >= 12 ? 'text-amber-400' : 'text-slate-400'}`}>
                    {data.historic_stats.combo_3_rate}%
                  </div>
                  <div className="text-slate-600 text-[9px]">Combo 3</div>
                </div>
                <div className="p-1.5 rounded bg-slate-900/60">
                  <div className={`font-black text-sm ${data.historic_stats.top_8_rate >= 20 ? 'text-emerald-400' : 'text-slate-400'}`}>
                    {data.historic_stats.top_8_rate}%
                  </div>
                  <div className="text-slate-600 text-[9px]">Top 8</div>
                </div>
                <div className="p-1.5 rounded bg-slate-900/60">
                  <div className={`font-black text-sm ${data.historic_stats.top_12_rate >= 25 ? 'text-emerald-400' : 'text-slate-400'}`}>
                    {data.historic_stats.top_12_rate}%
                  </div>
                  <div className="text-slate-600 text-[9px]">Plan 12</div>
                </div>
                <div className="p-1.5 rounded bg-purple-950/40 border border-purple-500/40">
                  <div className="font-black text-base text-purple-300">{data.historic_stats.cuadricula_20_rate ?? '—'}%</div>
                  <div className="text-purple-300 text-[9px] font-bold">CUADRICULA 20</div>
                </div>
                <div className="p-1.5 rounded bg-slate-900/60">
                  <div className="font-black text-sm text-orange-400">{data.historic_stats.hot_terminal_rate}%</div>
                  <div className="text-slate-600 text-[9px]">Hot Term</div>
                </div>
              </div>
              <p className="text-slate-500 text-[10px] mt-2 text-center">
                Datos reales. La <strong className="text-purple-300">Cuadricula (terminales × decadas)</strong> tiene la mejor tasa real.
              </p>
            </div>
          )}

          {/* CUADRICULA V3 — Output PRINCIPAL nuevo */}
          {data.cuadricula?.numbers?.length > 0 && (
            <div className="p-3 rounded-lg bg-gradient-to-r from-purple-950/50 to-fuchsia-950/30 border border-purple-700/40" data-testid={`tauro-cuadricula-${lottery.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Target size={14} className="text-purple-400" />
                  <span className="text-purple-300 font-bold text-sm">Cuadricula V3.1 ({data.cuadricula.size} numeros)</span>
                </div>
                <Badge variant="outline" className="text-[9px] py-0 border-purple-700/40 text-purple-300">
                  {data.cuadricula.matrix}
                </Badge>
              </div>

              {/* Matriz visual: terminales (columnas) × decadas (filas) */}
              <div className="mb-2 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="text-purple-500 text-[9px] font-bold p-1"></th>
                      {data.cuadricula.hot_terminals.map(t => (
                        <th key={`th-${t}`} className="text-orange-400 text-[10px] font-black p-1">x{t}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.cuadricula.hot_decades.map(d => (
                      <tr key={`tr-${d}`}>
                        <td className="text-cyan-400 text-[10px] font-black p-1 text-center">{d}x</td>
                        {data.cuadricula.hot_terminals.map(t => {
                          const num = d * 10 + t;
                          const inCombo = data.combo?.includes(num);
                          const isStar = data.star_number === num;
                          return (
                            <td key={`td-${d}-${t}`} className="p-0.5">
                              <div className={`aspect-square rounded flex items-center justify-center font-black text-xs ${
                                isStar ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white shadow-lg shadow-orange-500/30'
                                : inCombo ? 'bg-orange-500/30 text-orange-200 border border-orange-500/50'
                                : 'bg-purple-900/50 text-purple-200 border border-purple-800/40'
                              }`}>
                                {String(num).padStart(2, '0')}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-slate-400 text-[10px]">
                <strong className="text-purple-300">Estrategia ampliada:</strong> 5 terminales × 5 decenas calientes = 25 números.
                <strong className="text-emerald-300"> ~57% acierto real</strong> en backtest (1.7x más cobertura que V3).
              </p>
            </div>
          )}

          {/* COBERTURA MAXIMA — 42 numeros, ~81% acierto */}
          {data.cobertura_maxima?.numbers?.length > 0 && (
            <details className="rounded-lg bg-gradient-to-r from-emerald-950/40 to-cyan-950/30 border border-emerald-700/40" data-testid={`tauro-coverage-${lottery.id}`}>
              <summary className="cursor-pointer p-3 flex items-center justify-between hover:bg-emerald-950/40">
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-emerald-400" />
                  <span className="text-emerald-300 font-bold text-sm">Modo Cobertura Maxima ({data.cobertura_maxima.size} numeros)</span>
                </div>
                <Badge variant="outline" className="text-[10px] py-0 border-emerald-500/50 text-emerald-300 font-bold">
                  ~{data.cobertura_maxima.expected_hit_rate}% acierto
                </Badge>
              </summary>
              <div className="p-3 border-t border-emerald-800/30">
                <div className="mb-2 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className="text-emerald-500 text-[9px] font-bold p-1"></th>
                        {data.cobertura_maxima.hot_terminals.map(t => (
                          <th key={`thx-${t}`} className="text-orange-400 text-[10px] font-black p-1">x{t}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.cobertura_maxima.hot_decades.map(d => (
                        <tr key={`trx-${d}`}>
                          <td className="text-cyan-400 text-[10px] font-black p-1 text-center">{d}x</td>
                          {data.cobertura_maxima.hot_terminals.map(t => {
                            const num = d * 10 + t;
                            const inCombo = data.combo?.includes(num);
                            const inGrid5 = data.cuadricula?.numbers?.includes(num);
                            return (
                              <td key={`tdx-${d}-${t}`} className="p-0.5">
                                <div className={`aspect-square rounded flex items-center justify-center font-black text-[10px] ${
                                  inCombo ? 'bg-orange-500/40 text-white border border-orange-400'
                                  : inGrid5 ? 'bg-purple-900/60 text-purple-100 border border-purple-700/60'
                                  : 'bg-emerald-900/40 text-emerald-200 border border-emerald-800/40'
                                }`}>
                                  {String(num).padStart(2, '0')}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-slate-400 text-[10px]">
                  <strong className="text-emerald-300">7 terminales × 6 decenas = 42 numeros.</strong> Jugando estos 42 numeros acertás al menos 1 en el <strong className="text-white">~81% de los sorteos</strong> (backtest 240 sorteos). Recomendado cuando quieras MAXIMA cobertura sobre lentes calientes.
                </p>
                <p className="text-slate-500 text-[10px] mt-1">
                  Morado = en cuadrícula 5×5 · Verde = adicional · Naranja = combo Sócrates
                </p>
              </div>
            </details>
          )}

          {/* PALES INTELIGENTES — Estrategia diversificada de bankroll */}
          {data.cuadricula?.pale_inteligente?.length > 0 && (
            <div className="p-3 rounded-lg bg-gradient-to-r from-cyan-950/40 to-teal-950/30 border border-cyan-700/40"
              data-testid={`tauro-pales-${lottery.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-cyan-400" />
                  <span className="text-cyan-300 font-bold text-sm">Pales Inteligentes (Estrategia Bankroll)</span>
                </div>
                <Badge variant="outline" className="text-[9px] py-0 border-cyan-700/40 text-cyan-300">
                  {data.cuadricula.pale_inteligente.length} pares
                </Badge>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                {data.cuadricula.pale_inteligente.map((pale, idx) => (
                  <div key={`pale-${idx}`} className="p-2 rounded-lg bg-slate-900/60 border border-cyan-800/30">
                    <div className="text-cyan-500 text-[9px] mb-1">Pale #{idx + 1}</div>
                    <div className="flex items-center gap-1">
                      <span className="flex-1 text-center bg-slate-800/60 rounded py-1 text-white font-bold text-sm">
                        {String(pale[0]).padStart(2, '0')}
                      </span>
                      <span className="text-cyan-400 text-xs font-bold">-</span>
                      <span className="flex-1 text-center bg-slate-800/60 rounded py-1 text-white font-bold text-sm">
                        {String(pale[1]).padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-slate-400 text-[10px]">
                <strong className="text-cyan-300">Estos 8 pales</strong> cubren las mejores combinaciones de la cuadrícula con diversidad máxima (cada par tiene distinta decena Y distinta terminal). Distribuye tu inversión en estos 8 pares para maximizar las chances de pegar pale (2 de 3 números).
              </p>
            </div>
          )}

          {/* PLAN MAESTRO — Top 10-12 numeros (output secundario) */}
          {data.play_plan?.length > 0 && (
            <div className="p-3 rounded-lg bg-gradient-to-r from-emerald-950/40 to-cyan-950/30 border border-emerald-700/40" data-testid={`tauro-plan-${lottery.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Target size={14} className="text-emerald-400" />
                  <span className="text-emerald-300 font-bold text-sm">Plan Maestro ({data.play_plan.length} numeros)</span>
                </div>
                <Badge variant="outline" className="text-[9px] py-0 border-emerald-700/40 text-emerald-300">
                  cubre {new Set(data.play_plan.map(n => n%10)).size} terminales
                </Badge>
              </div>
              <div className="grid grid-cols-6 gap-1.5">
                {data.play_plan.map((n) => {
                  const isCombo = data.combo?.includes(n);
                  const isStar = data.star_number === n;
                  return (
                    <div key={`pp-${n}`} className={`aspect-square rounded-lg flex items-center justify-center font-black text-sm border ${
                      isStar ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white border-yellow-300 shadow-lg shadow-orange-500/30 relative'
                      : isCombo ? 'bg-orange-500/25 text-orange-200 border-orange-500/50'
                      : 'bg-slate-800/60 text-slate-300 border-slate-700/50'
                    }`}>
                      {isStar && <Star size={8} className="absolute -top-1 -right-1 text-yellow-200 fill-yellow-200" />}
                      {String(n).padStart(2, '0')}
                    </div>
                  );
                })}
              </div>
              <p className="text-slate-400 text-[10px] mt-2">
                <strong className="text-emerald-300">Recomendado:</strong> juega los 12 numeros del Plan Maestro distribuyendo tu bankroll. Cobertura ~25-30% en backtest real.
              </p>
            </div>
          )}

          {/* Star number + Strategy */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-orange-950/40 to-rose-950/30 border border-orange-800/30">
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 via-orange-500 to-red-600 flex items-center justify-center shadow-xl shadow-orange-500/40">
                <span className="text-white font-black text-2xl">{String(data.star_number).padStart(2, '0')}</span>
              </div>
              <Star className="absolute -top-1 -right-1 text-yellow-300 fill-yellow-300" size={18} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-orange-300 font-bold text-sm">Numero Estrella</span>
                {risk && (
                  <Badge variant="outline" className={`text-[9px] py-0 ${risk.border} ${risk.color}`}>
                    Riesgo {risk.label}
                  </Badge>
                )}
              </div>
              <div className="flex gap-3 text-[10px]">
                <span className="text-slate-400">Consenso Sócrates: <strong className="text-white">{data.consensus_score}/100</strong></span>
                <span className="text-slate-400">Acuerdo: <strong className="text-purple-300">{data.llm_agreement}</strong></span>
                <span className="text-slate-400">Sorteos: <strong className="text-white">{data.draws_analyzed}</strong></span>
              </div>
            </div>
          </div>

          {/* Bankroll advice */}
          {data.bankroll && (
            <div className="p-3 rounded-lg bg-emerald-950/20 border border-emerald-800/30" data-testid={`tauro-bankroll-${lottery.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <Wallet size={14} className="text-emerald-400" />
                <span className="text-emerald-300 font-bold text-xs">
                  Distribucion de Bankroll ({data.bankroll.strategy})
                </span>
              </div>
              <p className="text-slate-300 text-[11px] mb-2">{data.bankroll.message}</p>
              <div className="flex gap-1">
                {Object.entries(data.bankroll.pct_100).map(([k, v]) => (
                  <div key={k} className="flex-1 p-1.5 rounded bg-slate-800/40 text-center">
                    <div className="text-white font-black text-sm">{v}%</div>
                    <div className="text-slate-500 text-[9px] capitalize">{k.replace('_', ' ')}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pales + Tripleta */}
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 rounded-lg bg-slate-800/40">
              <div className="text-slate-500 text-[10px] mb-1">Pales recomendados</div>
              <div className="space-y-1">
                {data.pales?.map((p, i) => (
                  <div key={`p-${i}`} className="text-white font-mono text-xs">
                    {p.map(n => String(n).padStart(2, '0')).join(' - ')}
                  </div>
                ))}
              </div>
            </div>
            <div className="p-2 rounded-lg bg-slate-800/40">
              <div className="text-slate-500 text-[10px] mb-1">Tripleta</div>
              <div className="text-white font-mono text-sm font-bold">
                {data.tripleta?.map(n => String(n).padStart(2, '0')).join(' - ')}
              </div>
            </div>
          </div>

          {/* Strong numbers + Hot terminals */}
          <div className="space-y-2">
            <div>
              <div className="text-slate-500 text-[10px] mb-1 flex items-center gap-1">
                <Zap size={10} /> Numeros Fuertes (para complementar)
              </div>
              <div className="flex flex-wrap gap-1">
                {data.strong_numbers?.map(s => (
                  <div key={`s-${s.number}`} className={`px-2 py-1 rounded-lg text-xs font-bold border ${
                    s.source === 'poderoso' ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                    : 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
                  }`}>
                    {String(s.number).padStart(2, '0')}
                  </div>
                ))}
              </div>
            </div>
            {data.hot_terminals?.length > 0 && (
              <div className="p-2 rounded-lg bg-orange-950/30 border border-orange-800/30">
                <div className="text-orange-300 text-[10px] mb-1 flex items-center gap-1 font-bold">
                  <Flame size={10} /> Terminales Calientes
                </div>
                <div className="flex gap-2">
                  {data.hot_terminals.map(t => (
                    <div key={`ht-${t}`} className="flex-1 p-1.5 rounded bg-orange-500/15 border border-orange-500/40 text-center">
                      <div className="text-orange-300 font-black text-lg">x{t}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Daily trend */}
          {data.daily_trend && (
            <div className="p-2 rounded-lg bg-slate-800/30">
              <div className="flex items-center gap-1 mb-1">
                <Activity size={11} className="text-cyan-400" />
                <span className="text-cyan-300 text-[10px] font-bold">Tendencia de los Ultimos 5 Sorteos</span>
              </div>
              <div className="grid grid-cols-5 gap-1 text-center">
                <div>
                  <div className="text-white font-bold text-xs">{data.daily_trend.evens}%</div>
                  <div className="text-slate-600 text-[9px]">pares</div>
                </div>
                <div>
                  <div className="text-white font-bold text-xs">{data.daily_trend.odds}%</div>
                  <div className="text-slate-600 text-[9px]">impares</div>
                </div>
                <div>
                  <div className="text-white font-bold text-xs">{data.daily_trend.low_range}%</div>
                  <div className="text-slate-600 text-[9px]">0-33</div>
                </div>
                <div>
                  <div className="text-white font-bold text-xs">{data.daily_trend.mid_range}%</div>
                  <div className="text-slate-600 text-[9px]">34-66</div>
                </div>
                <div>
                  <div className="text-white font-bold text-xs">{data.daily_trend.high_range}%</div>
                  <div className="text-slate-600 text-[9px]">67-99</div>
                </div>
              </div>
            </div>
          )}

          {/* Reasonings per model */}
          {data.reasonings?.length > 0 && (
            <details className="rounded-lg bg-slate-800/30">
              <summary className="cursor-pointer p-2 text-[11px] text-purple-400 hover:text-purple-300 flex items-center gap-1">
                <Brain size={11} /> Razonamiento de los 3 núcleos Sócrates
              </summary>
              <div className="p-2 space-y-2">
                {data.reasonings.map((r, i) => (
                  <div key={`r-${i}`} className="p-2 rounded bg-slate-900/50 border-l-2 border-purple-500/50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-purple-300 font-bold text-[10px]">{r.model}</span>
                      <span className="text-slate-600 text-[9px]">{r.strategy}</span>
                    </div>
                    <p className="text-slate-300 text-[11px] leading-relaxed">{r.text}</p>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* LLM picks transparency */}
          {data.llm_picks?.length > 0 && (
            <details className="rounded-lg bg-slate-800/20">
              <summary className="cursor-pointer p-2 text-[11px] text-slate-400 hover:text-slate-300">
                Ver picks individuales de cada núcleo Sócrates
              </summary>
              <div className="p-2 space-y-2">
                {data.llm_picks.map((lp, i) => (
                  <div key={`lp-${i}`} className="p-2 rounded bg-slate-900/40 border-l-2 border-purple-500/40">
                    <div className="flex items-center justify-between mb-1 text-[10px]">
                      <span className="text-purple-300 font-bold">{lp.model}</span>
                      {!lp.error && (
                        <span className="text-slate-500">
                          Sniper {lp.confidence_sniper ?? lp.confidence ?? 0}% · Alerta {lp.confidence_alerta ?? '—'}%
                        </span>
                      )}
                    </div>
                    {lp.error ? (
                      <span className="text-red-400 text-[10px]">Error: {lp.error}</span>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-orange-400 text-[9px] font-bold w-12">SNIPER:</span>
                          {(lp.sniper_3 || lp.combo || []).map(n => (
                            <span key={`sn-${i}-${n}`} className="px-1.5 py-0.5 rounded bg-orange-500/20 border border-orange-500/40 text-orange-200 font-mono text-[10px] font-bold">
                              {String(n).padStart(2, '0')}
                            </span>
                          ))}
                        </div>
                        {lp.alerta_10?.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="text-emerald-400 text-[9px] font-bold w-12">ALERTA:</span>
                            {lp.alerta_10.map(n => (
                              <span key={`al-${i}-${n}`} className="px-1 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 font-mono text-[10px]">
                                {String(n).padStart(2, '0')}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}

          <div className="text-[10px] text-slate-500">
            Ultimo sorteo: {data.last_draw?.map(n => String(n).padStart(2, '0')).join(' - ')}
          </div>
        </div>
      )}
    </div>
  );
};

export default function TauroPage() {
  const { user, token } = useAuth();
  const [lotteries, setLotteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [bolaFuego, setBolaFuego] = useState(null);
  const [bolaLoading, setBolaLoading] = useState(false);

  const loadLotteries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lotteries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rd = (res.data || []).filter(l => l.is_active && l.company !== 'Americanas');
      setLotteries(rd);
    } catch (e) {
      toast.error('Error cargando loterias');
    }
    setLoading(false);
  }, [token]);

  const [bolaPolls, setBolaPolls] = useState(0);
  const [bolaTrack, setBolaTrack] = useState(null);

  const loadBolaTrack = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/tauro/bola-de-fuego/track-record`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000,
      });
      setBolaTrack(res.data);
    } catch (e) { /* silencioso */ }
  }, [token]);

  const loadBolaFuego = useCallback(async (pollCount = 0) => {
    if (!token) return;
    if (pollCount === 0) setBolaLoading(true);
    try {
      const res = await axios.get(`${API}/tauro/bola-de-fuego`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      setBolaFuego(res.data);
      setBolaPolls(pollCount);
      // Polling con limite: hasta 8 reintentos cada 30s = 4 minutos max
      if (res.data?.status === 'processing' && pollCount < 8) {
        setTimeout(() => loadBolaFuego(pollCount + 1), 30000);
      }
    } catch (e) {
      // Si falla, no bloquear UI — ocultar el banner
      setBolaFuego({ status: 'unavailable' });
    }
    setBolaLoading(false);
  }, [token]);

  useEffect(() => {
    if (token) {
      loadLotteries();
      loadBolaFuego();
      loadBolaTrack();
    }
  }, [token, loadLotteries, loadBolaFuego, loadBolaTrack]);

  const filtered = search
    ? lotteries.filter(p => p.name?.toLowerCase().includes(search.toLowerCase()))
    : lotteries;

  if (!user) return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
      <p className="text-slate-400">Inicia sesion</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0e1a]" data-testid="tauro-page">
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-6 pt-20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white"><ArrowLeft size={16} /></Button>
            </Link>
            <div>
              <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-400 to-rose-500 flex items-center gap-2">
                <Brain className="text-orange-400" size={26} /> TAURO
              </h1>
              <p className="text-slate-500 text-sm">Super analista matematico · 3 núcleos Sócrates en consenso · Solo RD</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadLotteries} disabled={loading}
            className="border-orange-700/40 text-orange-400 hover:text-white" data-testid="tauro-refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        <div className="mb-4">
          <Link to="/tauro-performance">
            <Button variant="outline"
              className="w-full bg-gradient-to-r from-emerald-950/40 to-cyan-950/30 border-emerald-700/40 text-emerald-300 hover:text-white hover:bg-emerald-900/40 font-bold"
              data-testid="tauro-performance-link">
              <TrendingUp size={16} className="mr-2" /> Ver Tauro Performance — Track Record Real
            </Button>
          </Link>
        </div>

        {/* ===== BOLA DE FUEGO DEL DIA 🔥 ===== */}
        {bolaLoading && bolaPolls === 0 && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-orange-950/30 border border-orange-700/30 flex items-center gap-2"
            data-testid="bola-fuego-loading">
            <Loader2 className="animate-spin text-orange-400" size={14} />
            <span className="text-orange-300 text-xs">Cargando Bola de Fuego del día...</span>
          </div>
        )}

        {!bolaLoading && bolaFuego?.status === 'processing' && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-gradient-to-r from-orange-950/30 to-amber-950/20 border border-orange-700/30"
            data-testid="bola-fuego-processing">
            <div className="flex items-start gap-2">
              <Loader2 className="animate-spin text-orange-400 mt-0.5 shrink-0" size={14} />
              <div className="text-xs flex-1">
                <div className="text-orange-200 font-bold">Tauro está procesando las 16 loterías…</div>
                <div className="text-orange-400/70 mt-0.5">
                  Esto solo pasa una vez al día. Mientras tanto, expande cualquier lotería abajo para ver sus predicciones individuales.
                  {bolaPolls > 0 && ` (auto-check ${bolaPolls}/8)`}
                </div>
              </div>
            </div>
          </div>
        )}

        {!bolaLoading && bolaFuego?.available && (
          <div className="mb-6 relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-950 via-orange-950 to-amber-950 border-2 border-orange-500/50 shadow-2xl shadow-orange-900/40"
            data-testid="bola-fuego-banner"
            style={{ animation: 'fadeIn 0.6s ease-out' }}>
            {/* Glow background */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(251,146,60,0.25),transparent_50%)] pointer-events-none" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(239,68,68,0.2),transparent_50%)] pointer-events-none" />

            <div className="relative p-5">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400 via-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/50">
                      <Flame className="text-white" size={28} fill="white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-300 rounded-full animate-pulse" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-black bg-gradient-to-r from-yellow-300 via-orange-400 to-red-500 bg-clip-text text-transparent tracking-tight">
                        BOLA DE FUEGO
                      </h2>
                      <Badge variant="outline" className="border-yellow-400/50 text-yellow-300 text-[9px] font-black uppercase tracking-widest">
                        Hoy
                      </Badge>
                    </div>
                    <p className="text-orange-200/80 text-xs">Elegida por aciertos REALES verificados en sorteos recientes</p>
                  </div>
                </div>
                {bolaFuego.meets_criteria ? (
                  <Badge className="bg-emerald-500/20 border border-emerald-400/50 text-emerald-300 font-bold text-[10px] uppercase">
                    ✓ Criterios cumplidos
                  </Badge>
                ) : (
                  <Badge className="bg-amber-500/20 border border-amber-400/50 text-amber-300 font-bold text-[10px] uppercase">
                    Mejor pick disponible
                  </Badge>
                )}
              </div>

              {/* Lottery info */}
              <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-black/30 border border-orange-500/20">
                <div className="w-1.5 h-12 rounded-full" style={{ backgroundColor: bolaFuego.lottery?.color || '#fb923c' }} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-black text-lg">{bolaFuego.lottery?.name}</h3>
                    <Badge variant="outline" className="border-orange-700/40 text-orange-300 text-[9px]">
                      {bolaFuego.lottery?.company}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px]">
                    <span className="text-orange-300/80 flex items-center gap-1">
                      <Clock size={11} /> Sorteo a las {bolaFuego.lottery?.draw_time}
                    </span>
                    <span className="text-slate-400">·</span>
                    <span className="text-orange-300/80">Score Fuego: <strong className="text-white">{bolaFuego.score}</strong></span>
                  </div>
                </div>
                <Link to={`/tauro`}>
                  <Button size="sm" className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 text-white border-0 font-bold shadow-lg shadow-orange-900/50"
                    data-testid="bola-fuego-view-btn"
                    onClick={() => {
                      // Scroll to the lottery card
                      setTimeout(() => {
                        const el = document.querySelector(`[data-testid="tauro-${bolaFuego.lottery?.id}"]`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 100);
                    }}>
                    Ver detalle
                  </Button>
                </Link>
              </div>

              {/* Numbers */}
              <div className="mb-4">
                <div className="text-orange-300 text-[10px] font-bold uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <Target size={12} /> Sniper Premio Mayor
                </div>
                <div className="flex items-center justify-center gap-3">
                  {bolaFuego.sniper_3?.map((n, i) => (
                    <div key={`bf-sn-${n}-${i}`} className="relative">
                      <div className={`w-24 h-24 rounded-3xl flex items-center justify-center shadow-2xl ${
                        n === bolaFuego.star_number
                          ? 'bg-gradient-to-br from-yellow-300 via-orange-500 to-red-600 shadow-orange-500/70 ring-4 ring-yellow-300/40'
                          : 'bg-gradient-to-br from-orange-500 to-rose-700 shadow-rose-700/50'
                      }`}>
                        <span className="text-white font-black text-4xl drop-shadow-lg">{String(n).padStart(2, '0')}</span>
                      </div>
                      {n === bolaFuego.star_number && (
                        <div className="absolute -top-2 -right-2 bg-yellow-300 text-amber-900 text-[9px] font-black px-2 py-0.5 rounded-full shadow-md flex items-center gap-0.5">
                          <Star size={10} className="fill-amber-900" /> ESTRELLA
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Seleccion por aciertos reales (Fire V2) */}
              {bolaFuego.real_backtest?.draws_tested > 0 && (
                <div className="mb-4 p-3 rounded-xl bg-emerald-950/50 border border-emerald-500/30" data-testid="bola-fuego-real-backtest">
                  <div className="text-emerald-300 text-[10px] font-bold uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                    <Target size={11} /> Por que esta loteria — Aciertos reales del metodo
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2 rounded-lg bg-black/40 text-center">
                      <div className="text-emerald-400 font-black text-base">{bolaFuego.real_backtest.alerta_any_rate}%</div>
                      <div className="text-emerald-200/60 text-[9px] uppercase">Acierto real ({bolaFuego.real_backtest.draws_tested} sorteos)</div>
                    </div>
                    <div className="p-2 rounded-lg bg-black/40 text-center">
                      <div className="text-yellow-300 font-black text-base">{bolaFuego.real_backtest.recent_hits_5}/5</div>
                      <div className="text-emerald-200/60 text-[9px] uppercase">Racha reciente</div>
                    </div>
                    <div className="p-2 rounded-lg bg-black/40 text-center">
                      <div className="text-orange-300 font-black text-base">{bolaFuego.real_backtest.sniper_any_rate}%</div>
                      <div className="text-emerald-200/60 text-[9px] uppercase">Sniper real</div>
                    </div>
                  </div>
                  {bolaFuego.real_backtest.hits_series?.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <span className="text-emerald-200/50 text-[9px] uppercase">Ultimos sorteos:</span>
                      {bolaFuego.real_backtest.hits_series.map((h, i) => (
                        <span key={`bf-hs-${i}`} className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-black ${
                          h ? 'bg-emerald-500/80 text-white' : 'bg-slate-700/80 text-slate-400'
                        }`}>{h ? '✓' : '✗'}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Stats grid */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="p-2 rounded-lg bg-black/40 text-center">
                  <div className="text-orange-400 font-black text-base">{bolaFuego.sniper_confidence}%</div>
                  <div className="text-orange-200/60 text-[9px] uppercase">Sniper</div>
                </div>
                <div className="p-2 rounded-lg bg-black/40 text-center">
                  <div className="text-emerald-400 font-black text-base">{bolaFuego.alerta_confidence}%</div>
                  <div className="text-orange-200/60 text-[9px] uppercase">Alerta 10</div>
                </div>
                <div className="p-2 rounded-lg bg-black/40 text-center">
                  <div className="text-yellow-300 font-black text-base">{bolaFuego.llm_agreement}</div>
                  <div className="text-orange-200/60 text-[9px] uppercase">Acuerdo núcleos</div>
                </div>
                <div className="p-2 rounded-lg bg-black/40 text-center">
                  <div className="text-purple-300 font-black text-base">{bolaFuego.consensus_score}/100</div>
                  <div className="text-orange-200/60 text-[9px] uppercase">Consenso</div>
                </div>
              </div>

              {/* Alerta 10 mini */}
              {bolaFuego.alerta_10?.length > 0 && (
                <div className="mb-3">
                  <div className="text-emerald-300 text-[10px] font-bold uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                    <Sparkles size={11} /> Alerta 10 — Cobertura completa
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {bolaFuego.alerta_10.map(n => {
                      const isSniper = bolaFuego.sniper_3?.includes(n);
                      const isStar = n === bolaFuego.star_number;
                      return (
                        <div key={`bf-al-${n}`} className={`w-10 h-10 rounded-lg flex items-center justify-center font-black text-sm border ${
                          isStar ? 'bg-yellow-400 text-amber-900 border-yellow-300'
                          : isSniper ? 'bg-orange-500/30 text-orange-100 border-orange-500/50'
                          : 'bg-emerald-900/40 text-emerald-200 border-emerald-700/30'
                        }`}>
                          {String(n).padStart(2, '0')}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Footer: runner-up + total evaluated */}
              <div className="flex items-center justify-between pt-3 border-t border-orange-500/20 text-[10px]">
                {bolaFuego.runner_up?.name ? (
                  <div className="text-orange-200/60">
                    Segundo lugar: <strong className="text-orange-300">{bolaFuego.runner_up.name}</strong> · {bolaFuego.runner_up.sniper_confidence}% · score {bolaFuego.runner_up.score}
                  </div>
                ) : <div />}
                <div className="text-orange-200/60">
                  Evaluadas: <strong className="text-white">{bolaFuego.total_evaluated}</strong> loterias RD
                </div>
              </div>

              {/* Track Record REAL de la Bola de Fuego */}
              {bolaTrack?.summary && bolaTrack.summary.total_days > 0 && (
                <div className="mt-3 pt-3 border-t border-orange-500/20" data-testid="bola-fuego-track-record">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-yellow-300 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                      <Activity size={11} /> Track record real
                    </div>
                    <div className="text-[11px]">
                      <span className="text-orange-200/70">Ultimos 7 dias: </span>
                      <strong className={bolaTrack.summary.hits_any_7 > 0 ? 'text-emerald-400' : 'text-slate-300'}>
                        {bolaTrack.summary.hits_any_7}/{bolaTrack.summary.days_verified_7} aciertos
                      </strong>
                      <span className="text-slate-500"> · total {bolaTrack.summary.total_hits}/{bolaTrack.summary.total_days}</span>
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {bolaTrack.history.slice(0, 7).map(h => (
                      <div key={`bt-${h.date}`} className={`px-2 py-1 rounded-lg border text-[9px] ${
                        !h.verified ? 'bg-slate-800/60 border-slate-600/40 text-slate-400'
                        : h.hit_any ? 'bg-emerald-900/50 border-emerald-500/40 text-emerald-300'
                        : 'bg-rose-950/40 border-rose-700/30 text-rose-300/80'
                      }`}>
                        <div className="font-bold">{h.date?.slice(5)}</div>
                        <div>{!h.verified ? 'Pendiente' : h.hit_any ? `✓ ${h.alerta_hits?.map(n => String(n).padStart(2, '0')).join(', ')}` : '✗ Sin acierto'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hero: Sócrates engines */}
        <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-orange-950/40 via-red-950/30 to-rose-950/40 border border-orange-700/40"
          data-testid="tauro-hero">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="text-orange-400" size={20} />
            <div>
              <h2 className="text-white font-black text-lg">Consenso de 3 Motores Sócrates</h2>
              <p className="text-slate-400 text-xs">Cada lotería es analizada por los 3 motores en paralelo</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="p-2 rounded-lg bg-slate-900/60 border border-emerald-700/30">
              <div className="text-emerald-400 text-[10px] font-bold uppercase">Sócrates</div>
              <div className="text-white font-bold text-sm">Núcleo Alfa</div>
              <div className="text-slate-500 text-[9px]">Razonamiento profundo</div>
            </div>
            <div className="p-2 rounded-lg bg-slate-900/60 border border-amber-700/30">
              <div className="text-amber-400 text-[10px] font-bold uppercase">Sócrates</div>
              <div className="text-white font-bold text-sm">Núcleo Beta</div>
              <div className="text-slate-500 text-[9px]">Matematica paso a paso</div>
            </div>
            <div className="p-2 rounded-lg bg-slate-900/60 border border-blue-700/30">
              <div className="text-blue-400 text-[10px] font-bold uppercase">Sócrates</div>
              <div className="text-white font-bold text-sm">Núcleo Gamma</div>
              <div className="text-slate-500 text-[9px]">Analisis balanceado</div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
            <div className="flex items-center gap-1 text-slate-400">
              <Target size={11} className="text-orange-400" />
              <span>Combo 3 numeros con consenso</span>
            </div>
            <div className="flex items-center gap-1 text-slate-400">
              <Wallet size={11} className="text-emerald-400" />
              <span>Bankroll personalizado</span>
            </div>
            <div className="flex items-center gap-1 text-slate-400">
              <Activity size={11} className="text-cyan-400" />
              <span>Tendencia del dia</span>
            </div>
          </div>
        </div>

        <div className="mb-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
          <div className="flex items-start gap-2 text-xs text-slate-400">
            <Shield size={14} className="text-orange-400 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p>
                <strong className="text-orange-300">TAURO V4</strong> ahora tiene <strong className="text-indigo-300">Inteligencia Adaptativa</strong> + auto-aprendizaje por loteria.
                <br />Detecta el modo (caliente/frio/transicion), elige la mejor senal historica, y ofrece <strong className="text-emerald-300">Combos Especificos 5, 7, Tripleta Frecuente y Cold Comeback</strong>.
              </p>
              <p className="text-slate-500 text-[11px]">
                <strong className="text-indigo-300">Auto-aprendizaje:</strong> Tauro guarda cada predicción y mide su acierto real. A medida que pasan los dias su track record se vuelve mas preciso por loteria. Combinado con Cuadricula 5×5 (~57%) y Cobertura Maxima 7×6 (~81%) ofrece la cobertura mas inteligente.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-4 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input placeholder="Buscar loteria dominicana..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="bg-slate-900/70 border-slate-800 text-white pl-10" data-testid="tauro-search" />
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-20 gap-3">
            <Loader2 className="animate-spin text-orange-400" size={36} />
            <p className="text-slate-500 text-sm">Cargando loterias dominicanas...</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="tauro-list">
            {filtered.map((l, i) => <LotteryCard key={l.id} lottery={l} token={token} index={i} />)}
            {filtered.length === 0 && <div className="text-center py-12 text-slate-500">No se encontraron loterias</div>}
          </div>
        )}
      </div>
    </div>
  );
}
